import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { LiliLink, LiliLinkType } from '@lili/lili-frontend-common';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/svg/shared/ic-shared-right-arrow.svg';
import { cvsIcon, dollarTreeIcon, seven11Icon, wIcon, walmartIcon } from '../../../assets/svg';
import { Layout } from '../../../components/Layout/Layout';
import { AUTOMATION_TEST_ID } from '../../../constants/general';
import { ActionModal } from '../../../DesignSystem/ActionModal';
import Button from '../../../generic-components/Button';
import CardRow from '../../../generic-components/CardRow';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import Heading from '../../../generic-components/Heading';
import SubContent from '../../../generic-components/Layout/SubContent';
import { VIEWS_MAP } from '../../../router/constants';
import { FIND_ATM_LOCATION_LINK } from '../constants';
import { reportActOnSpeedBump, reportViewSpeedBump } from '../../../analytics/speedBump.analytics';

const CashDeposit = () => {
  const ICONS_ROW = [wIcon, seven11Icon, walmartIcon, dollarTreeIcon, cvsIcon];
  const { t } = useTranslation();
  const [showLeaveWarning, setShowLeaveWarning] = useState<boolean>(false);

  const handleShowSpeedBump = () => {
    setShowLeaveWarning(true);
    reportViewSpeedBump('Cash Deposit');
  };

  const handleRedirectOutside = () => {
    reportActOnSpeedBump('Continue');
    window.open(FIND_ATM_LOCATION_LINK, '_blank');
    setShowLeaveWarning(false);
  };

  const handleCancelRedirectOutside = () => {
    reportActOnSpeedBump('Back');
    setShowLeaveWarning(false);
  };

  return (
    <Layout wrapperClassName='pt-[80px] pl-[72px] w-screen pr-[32px] ml-[100px] pc-md:ml-[256px]'>
      <Heading title={t('CASH_DEPOSIT_PAGE_HEADER')} className='font-metricRSemibold text-bw08 text-[20px] mb-[24px]' />
      <ContentWrapper>
        <SubContent
          className='flex flex-col items-left bg-bw00 rounded-lg w-[456px] pt-10 pb-[] pl-10 h-[570px]'
          testId={`${AUTOMATION_TEST_ID}__cash-deposit`}
        >
          <CardRow
            cards={ICONS_ROW}
            cardClassName='w-[64px] h-[64px]'
            containerClassName='w-full mb-[40px] flex justify-between pr-10'
          />
          <span
            className='text-[20px] h-[28px] inline-flex items-center font-metricMedium text-bw08 mb-[15px]'
            data-testid={`${AUTOMATION_TEST_ID}__cash-deposit__title`}
          >
            {t('CASH_DEPOSIT_PAGE_SUBHEADER')}
          </span>
          <div className='flex flex-col'>
            <div className='flex mb-[13px]' data-testid={`${AUTOMATION_TEST_ID}__cash-deposit__bullet1`}>
              <span className='mr-[8px] w-[20px] h-[26px] inline-flex items-center justify-center font-metricSemibold text-action03 text-[22px]'>
                {t('CASH_DEPOSIT_PAGE_FIRST_STEP_NUMBER')}
              </span>
              <div className='h-[70px] flex flex-col mt-[4px] text-base text-bw07'>
                <span className='font-metricMedium'>{t('CASH_DEPOSIT_PAGE_FIRST_STEP_HEADER')}</span>
                <span className='font-metric whitespace-pre-line'>{t('CASH_DEPOSIT_PAGE_FIRST_STEP_TEXT')}</span>
              </div>
            </div>
            <div className='flex mb-[13px]' data-testid={`${AUTOMATION_TEST_ID}__cash-deposit__bullet2`}>
              <span className='mr-[8px] w-[20px] h-[26px] inline-flex items-center justify-center font-metricSemibold text-action03 text-[22px]'>
                {t('CASH_DEPOSIT_PAGE_SECOND_STEP_NUMBER')}
              </span>
              <div className='h-[70px] flex flex-col mt-[4px] text-base text-bw07'>
                <span className='font-metricMedium'>{t('CASH_DEPOSIT_PAGE_SECOND_STEP_HEADER')}</span>
                <Trans i18nKey='CASH_DEPOSIT_PAGE_SECOND_STEP_TEXT' className='font-metric whitespace-pre-line'>
                  For updated cash deposit limits,
                  <LiliLink
                    id='cash-deposit-transaction-limits'
                    title='visit our transaction limits page'
                    href={`/${VIEWS_MAP.ACCOUNT_LIMITS}`}
                    type={LiliLinkType.QUIET}
                  />
                </Trans>
              </div>
            </div>
            <div className='flex' data-testid={`${AUTOMATION_TEST_ID}__cash-deposit__bullet3`}>
              <span className='mr-[8px] w-[20px] h-[26px] inline-flex items-center justify-center font-metricSemibold text-action03 text-[22px]'>
                {t('CASH_DEPOSIT_PAGE_THIRD_STEP_NUMBER')}
              </span>
              <div className='h-[45px] flex flex-col mt-[4px] text-base text-bw07'>
                <span className='font-metricMedium'>{t('CASH_DEPOSIT_PAGE_THIRD_STEP_HEADER')}</span>
                <span className='font-metric whitespace-pre-line'>{t('CASH_DEPOSIT_PAGE_THIRD_STEP_TEXT')}</span>
              </div>
            </div>
          </div>
          <Button
            childElement={<ArrowIcon className='ml-[0.438rem] w-[16px] text-action03 hover:text-action04 mt-[2px]' />}
            title={t('CASH_DEPOSIT_PAGE_FIND_DEPOSIT_LOCATIONS_BUTTON')}
            className='flex align-middle ml-[28px] w-fit h-[28px] mt-[32px] mb-[24px] flex mr-10 hover:text-action04 cursor-pointer text-action03 font-metricSemibold text-base'
            id='findDepositLocations'
            testId={`${AUTOMATION_TEST_ID}__cash-deposit__button-find-location`}
            onClick={handleShowSpeedBump}
          />
          {showLeaveWarning && (
            <ActionModal
              title={t('NAVIGATE_OUTSIDE_MODAL.TITLE')}
              description={t('NAVIGATE_OUTSIDE_MODAL.DESCRIPTION')}
              onSubmit={handleRedirectOutside}
              onCancel={handleCancelRedirectOutside}
              cancelButtonTitle={t('NAVIGATE_OUTSIDE_MODAL.CANCEL')}
              mainButtonTitle={t('NAVIGATE_OUTSIDE_MODAL.CONTINUE')}
              disclaimerText={t('NAVIGATE_OUTSIDE_MODAL.DISCLAIMER')}
            />
          )}
          <span
            className='ml-[28px] mr-10 w-[328] h-[36px] inline-flex items-center text-left text-bw07 text-sm font-metric whitespace-pre-line leading-[17px]'
            data-testid={`${AUTOMATION_TEST_ID}__cash-deposit__disclaimer`}
          >
            {t('CASH_DEPOSIT_PAGE_FOOTER_TEXT')}
          </span>
        </SubContent>
      </ContentWrapper>
    </Layout>
  );
};

export default CashDeposit;
